// Production-Ready Summary Viewer
// Features: Executive summary card, key takeaways, sections, reading progress, export (PDF/DOCX), copy to clipboard

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Sparkles,
  Download,
  FileText,
  FileImage,
  Copy,
  Check,
  Clock,
  Hash,
  BookOpen,
  ChevronRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import type { SummaryResponse } from '@/lib/content-studio/types';
import { useContentExporter } from '@/hooks/useContentExporter';

interface SummaryViewerProps {
  summary: SummaryResponse;
  className?: string;
}

// Accent colours for alternating section borders
const SECTION_ACCENTS = [
  'border-l-blue-500',
  'border-l-violet-500',
  'border-l-amber-500',
  'border-l-rose-500',
  'border-l-cyan-500',
  'border-l-emerald-500',
  'border-l-orange-500',
  'border-l-indigo-500',
];

export function SummaryViewer({ summary, className = '' }: SummaryViewerProps) {
  const [copied, setCopied] = useState(false);
  const [readingProgress, setReadingProgress] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { exportToDocx, exportToPdf, isExporting } = useContentExporter();

  // Estimated read time (average 200 words per minute)
  const estimatedReadTime = useMemo(() => {
    const minutes = Math.max(1, Math.ceil(summary.wordCount / 200));
    return minutes;
  }, [summary.wordCount]);

  // Build full text for clipboard
  const fullText = useMemo(() => {
    const parts: string[] = [];
    parts.push(summary.title);
    parts.push('');
    parts.push('Executive Summary');
    parts.push(summary.executiveSummary);
    parts.push('');
    parts.push('Key Takeaways');
    summary.keyPoints.forEach((point, i) => {
      parts.push(`${i + 1}. ${point}`);
    });
    summary.sections.forEach((section) => {
      parts.push('');
      parts.push(section.heading);
      parts.push(section.content);
    });
    return parts.join('\n');
  }, [summary]);

  // Handle scroll for reading progress
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight - container.clientHeight;
    if (scrollHeight <= 0) {
      setReadingProgress(100);
      return;
    }
    const progress = Math.min(100, Math.round((scrollTop / scrollHeight) * 100));
    setReadingProgress(progress);
  }, []);

  // Attach scroll listener to the ScrollArea viewport
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // ScrollArea renders a viewport div as the first child
    const viewport = container.querySelector('[data-radix-scroll-area-viewport]');
    if (!viewport) return;

    const onScroll = () => {
      const scrollTop = viewport.scrollTop;
      const scrollHeight = viewport.scrollHeight - viewport.clientHeight;
      if (scrollHeight <= 0) {
        setReadingProgress(100);
        return;
      }
      setReadingProgress(
        Math.min(100, Math.round((scrollTop / scrollHeight) * 100))
      );
    };

    viewport.addEventListener('scroll', onScroll, { passive: true });
    return () => viewport.removeEventListener('scroll', onScroll);
  }, []);

  // Copy to clipboard
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullText);
      setCopied(true);
      toast.success('Summary copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy to clipboard');
    }
  };

  // Export handlers
  const handleExportPdf = () => {
    exportToPdf('summary', summary);
  };

  const handleExportDocx = () => {
    exportToDocx('summary', summary);
  };

  return (
    <div className={cn('space-y-4', className)}>
      {/* Reading progress bar */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm pb-1">
        <Progress value={readingProgress} className="h-1" />
      </div>

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <h2 className="text-xl font-bold tracking-tight leading-tight">
            {summary.title}
          </h2>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <Badge variant="secondary" className="gap-1 text-xs">
              <Hash className="h-3 w-3" />
              {summary.wordCount.toLocaleString()} words
            </Badge>
            <Badge variant="outline" className="gap-1 text-xs">
              <Clock className="h-3 w-3" />
              {estimatedReadTime} min read
            </Badge>
            {summary.sections.length > 0 && (
              <Badge variant="outline" className="gap-1 text-xs">
                <BookOpen className="h-3 w-3" />
                {summary.sections.length} sections
              </Badge>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopy}
            className="gap-1.5"
          >
            {copied ? (
              <>
                <Check className="h-4 w-4 text-green-500" />
                Copied
              </>
            ) : (
              <>
                <Copy className="h-4 w-4" />
                Copy
              </>
            )}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={isExporting}
              >
                <Download className="h-4 w-4 mr-1.5" />
                {isExporting ? 'Exporting...' : 'Export'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleExportPdf}>
                <FileImage className="h-4 w-4 mr-2" />
                Export as PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportDocx}>
                <FileText className="h-4 w-4 mr-2" />
                Export as Word (.docx)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Scrollable content */}
      <ScrollArea className="max-h-[600px]" ref={scrollContainerRef}>
        <div className="space-y-6 pr-2">
          {/* Executive Summary — gradient card */}
          <Card className="overflow-hidden border-0 shadow-md">
            <div className="bg-gradient-to-br from-emerald-500/10 via-emerald-400/5 to-teal-500/10 dark:from-emerald-500/20 dark:via-emerald-400/10 dark:to-teal-500/15">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <div className="rounded-lg bg-emerald-500/10 p-1.5">
                    <Sparkles className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <span className="text-emerald-800 dark:text-emerald-300">
                    Executive Summary
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm leading-relaxed text-emerald-900/80 dark:text-emerald-100/80 whitespace-pre-wrap">
                  {summary.executiveSummary}
                </p>
              </CardContent>
            </div>
          </Card>

          {/* Key Takeaways */}
          {summary.keyPoints.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-base font-semibold flex items-center gap-2">
                <ChevronRight className="h-4 w-4 text-primary" />
                Key Takeaways
              </h3>
              <div className="grid gap-2">
                {summary.keyPoints.map((point, index) => (
                  <Card
                    key={index}
                    className="border-l-[3px] border-l-primary shadow-none"
                  >
                    <CardContent className="py-3 px-4">
                      <div className="flex items-start gap-3">
                        <span className="shrink-0 flex items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-bold h-6 w-6 mt-0.5">
                          <span className="mx-auto">{index + 1}</span>
                        </span>
                        <p className="text-sm leading-relaxed text-foreground/90 flex-1">
                          {point}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Sections */}
          {summary.sections.length > 0 && (
            <div className="space-y-3">
              <Separator />
              <div className="space-y-3">
                {summary.sections.map((section, index) => (
                  <Card
                    key={index}
                    className={cn(
                      'border-l-[3px] shadow-none',
                      SECTION_ACCENTS[index % SECTION_ACCENTS.length]
                    )}
                  >
                    <CardHeader className="pb-2 pt-4 px-4">
                      <h4 className="text-sm font-semibold text-foreground">
                        {section.heading}
                      </h4>
                    </CardHeader>
                    <CardContent className="pt-0 px-4 pb-4">
                      <p className="text-sm leading-relaxed text-muted-foreground whitespace-pre-wrap">
                        {section.content}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

export default SummaryViewer;
